import type { Intervention, Room } from '../../../types/weekOverview';
import {
  CALENDAR_HOUR_HEIGHT,
  CALENDAR_START_HOUR,
  CALENDAR_VERTICAL_PADDING,
  PROGRAM_LABEL_DISPLAY_NAMES,
  SESSION_TYPE_COLORS
} from '../../../constants/weekOverviewConstants';
import { SessionIcon } from './SessionIcon';
import styles from './InterventionCard.module.css';

interface InterventionCardProps {
  intervention: Intervention;
  room: Room | undefined;
  staffName: string | undefined;
  leftFraction: number;
  widthFraction: number;
  hourHeight?: number;
  isSelected?: boolean;
  onClick: (intervention: Intervention, anchorRect: DOMRect) => void;
}

// Horizontal breathing room between side-by-side cards and the column edges.
const COLUMN_GAP_PX = 3;
const COLUMN_OUTER_INSET_PX = 6;
// Cards never shrink below this, so very short sessions stay clickable.
const CARD_MIN_HEIGHT_PX = 28;
// Vertical gap so back-to-back sessions don't visually touch.
const CARD_BOTTOM_GAP_PX = 4;

export const InterventionCard = ({
  intervention,
  room,
  staffName,
  leftFraction,
  widthFraction,
  hourHeight = CALENDAR_HOUR_HEIGHT,
  isSelected = false,
  onClick
}: InterventionCardProps) => {
  const colors = SESSION_TYPE_COLORS[intervention.sessionType];
  const minutesFromStart = intervention.startMinutes - CALENDAR_START_HOUR * 60;
  const top = (minutesFromStart / 60) * hourHeight + CALENDAR_VERTICAL_PADDING;
  const heightMinutes = intervention.endMinutes - intervention.startMinutes;
  const height = Math.max(CARD_MIN_HEIGHT_PX, (heightMinutes / 60) * hourHeight - CARD_BOTTOM_GAP_PX);

  const leftPercent = leftFraction * 100;
  const widthPercent = widthFraction * 100;
  const leftCss = `calc(${leftPercent}% + ${COLUMN_OUTER_INSET_PX}px)`;
  const widthCss = `calc(${widthPercent}% - ${COLUMN_OUTER_INSET_PX + COLUMN_GAP_PX}px)`;

  const programLabel = intervention.programLabel
    ? PROGRAM_LABEL_DISPLAY_NAMES[intervention.programLabel]
    : '';

  const isMeetingOrLunch = intervention.sessionType === 'MEETING' || intervention.sessionType === 'LUNCH';

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
      data-intervention-card="true"
      style={{
        top,
        height,
        left: leftCss,
        width: widthCss,
        background: colors.background,
        borderColor: isSelected ? colors.text : colors.border,
        color: colors.text
      }}
      onClick={(e) => onClick(intervention, e.currentTarget.getBoundingClientRect())}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(intervention, e.currentTarget.getBoundingClientRect());
        }
      }}
    >
      <div className={styles.headerRow}>
        <span className={styles.title}>
          <SessionIcon sessionType={intervention.sessionType} />
          {intervention.title}
        </span>
        {programLabel && <span className={styles.programLabel}>{programLabel}</span>}
      </div>
      {!isMeetingOrLunch && room && <div className={styles.meta}>{room.name}</div>}
      {!isMeetingOrLunch && staffName && <div className={styles.meta}>⌘ {staffName}</div>}
    </div>
  );
};
